import { CorporationModel, CorporationSliceModel, GetCorporationDataResponse, initialCorporationValue, UpdateCorporationDataResponse } from "./corporation-interfaces";
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RejectedValue, ErrorResponse } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { API_BASE_URL as baseUri } from "config/env";

export const GetCorporation = createAsyncThunk<GetCorporationDataResponse, void, { rejectValue: RejectedValue }>(
    'Slice/CorporationSlice/GetCorporation',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/corporations/get', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetCorporationDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateCorporation = createAsyncThunk<UpdateCorporationDataResponse, CorporationModel, { rejectValue: RejectedValue }>(
    'Slice/CorporationSlice/UpdateCorporation',
    async (model: CorporationModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/corporations/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(model)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as UpdateCorporationDataResponse;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

const initialValue: CorporationSliceModel = {
    statusCode: '',
    isLoading: false,
    message: '',
    showNotification: false,
    corporation: null,
    employees: []
}

export const CorporationSlice = createSlice({
    name: 'CorporationSlice',
    initialState: initialValue,
    reducers: {
        resetCorporationSlice: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.message = '';
            state.showNotification = false;
            state.corporation = null;
            state.employees = []
        },
        resetCorporationStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
    },
    extraReducers(builder) {
        //GetCorporation
        builder.addCase(GetCorporation.fulfilled, (state, action: PayloadAction<GetCorporationDataResponse>) => {
            state.isLoading = false;
            state.message = '';
            state.employees = action.payload.employees;
            let corporation = { ceoId: action.payload.ceoId, name: action.payload.name }
            state.corporation = corporation;
        })
            .addCase(GetCorporation.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetCorporation.pending, state => {
                state.isLoading = true;
            })
        //UpdateCorporation
        builder.addCase(UpdateCorporation.fulfilled, (state, action: PayloadAction<UpdateCorporationDataResponse>) => {
            state.statusCode = "200";
            state.isLoading = false;
            state.message = 'The corporation is successfully updated.';
            state.showNotification = true;
        })
            .addCase(UpdateCorporation.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateCorporation.pending, state => {
                state.isLoading = true;
            })
    }
});

export const { resetCorporationStatusCode, resetCorporationSlice } = CorporationSlice.actions;
export default CorporationSlice.reducer;

